/**
 * previewSamples — one live preview bag per implemented registry component.
 *
 * Each bag is the uniform node bag the interpreter would have built —
 * `{ variant?, props?, data?, ctx?, slots?, children?, height? }` — so adding a
 * component to the registry is a two-line change with a live preview attached,
 * and every surface that wants to SHOW a component (the bootstrap cards, the
 * module graph's cards) renders the same real thing.
 *
 * Two shapes are new with the module model:
 *
 *  1. A CARD's fields are binds against the `item` scope, so a card sample
 *     carries `ctx.item` — the same row the grid would have put there. The
 *     sample is the real card reading a real row, not a mock of one.
 *  2. A GRID lays out a card TEMPLATE per row and needs the interpreter to do
 *     it. There is no interpreter here, so `props.renderTemplate` supplies the
 *     kit's documented seam: the same lookup + prop resolution the interpreter
 *     performs, minus connections and inspect markers (a preview has neither).
 *
 * Data-only module on purpose: it holds no component, so importing it from a
 * component file costs nothing at Fast Refresh time.
 */
import { myWardrobeMock } from '../mock/myWardrobe';
import { normalizeEntry } from '../viewir/fold';
import { lookupKit } from '../viewir/registry';
import { resolveProps } from '../viewir/props';
import { runDerive } from '../viewir/derives';
import { Padding, Surface } from '../viewir/components/primitives';

const items = myWardrobeMock.entries.map(normalizeEntry);
const pieces = items.filter((i) => i.type === 'piece');
// my-wardrobe is the only in-repo example mock; the medicine-box / nutritionist
// feeds that used to seed the medicine + meal card samples were removed with
// their blueprints. Those samples now read the wardrobe rows through the same
// real derives, so the bootstrap browser keeps building and rendering — a card
// that binds a field a garment row lacks simply degrades to what it carries.
const meds = pieces;
const meals = pieces;

// The rows a card actually binds are ANNOTATED rows — a card reads
// `item.status_tone` / `item.calorie_label`, never an expiry date or a calorie
// count. Running the real derives here is what makes a preview honest.
const medRows = runDerive('expiryStatus', meds, { soon_days: 30 });
const mealRows = runDerive('mealCard', meals, {});

const PHOTO = pieces[0]?.photo_url || '';
const photos = [
  { grid_id: 's1', session_id: 's1', photo_url: PHOTO, count: 2, piece_count: 2, model_label: 'Model' },
  { grid_id: 's2', session_id: 's2', photo_url: pieces[2]?.photo_url, count: 3, piece_count: 3, model_label: '' },
];

const baseCtx = {
  entries: meds, settings: { soon_days: 30 }, pending: { adds: [], updates: [] },
  resolveItems: (ids) => (ids || []).map((id) => pieces.find((p) => p.item_id === id)).filter(Boolean),
};

/** A month the calendar sample can actually show rows in (today's). */
const TODAY = new Date();
const isoOf = (day) => {
  const d = new Date(TODAY.getFullYear(), TODAY.getMonth(), day);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const DATED_ROWS = [
  { item_id: 'd1', date: isoOf(3), name: 'First' },
  { item_id: 'd2', date: isoOf(3), name: 'Second' },
  { item_id: 'd3', date: isoOf(11), name: 'Third' },
  { item_id: 'd4', date: isoOf(19), name: 'Fourth' },
];

/** A bag for a CARD: the card plus the one row the enclosing grid would scope. */
const withItem = (item) => ({ ...baseCtx, item });

/**
 * The studio's stand-in for the interpreter's `renderTemplate`: resolve the
 * card node's props against `{…ctx, item}` and render it. Signals go nowhere —
 * a preview has no connections to route them through.
 */
const makeTemplate = (ctx) => (node, { item, key, onSignal } = {}) => {
  if (!node) return null;
  const kit = lookupKit(node.type);
  if (!kit) return null;
  const Card = kit.component;
  const scoped = { ...ctx, item };
  return (
    <Card
      key={key}
      nodeId={key}
      node={node}
      variant={node.variant}
      props={resolveProps(node.props, kit, scoped)}
      data={item}
      ctx={scoped}
      emit={(signal, payload) => onSignal?.(signal, payload)}
      slots={{}}
    />
  );
};

const renderTemplate = makeTemplate(baseCtx);

/** The medicine card every medicine-box grid lays out — the real card spec. */
const MED_CARD = {
  type: 'Card',
  variant: 'media-top',
  props: {
    layout: 'media-top',
    aspect: '1/1',
    media: {
      src: { $: 'item.image_url' },
      ratio: '1/1',
      fit: 'cover',
      badge: { tone: { $: 'item.status_tone' }, dot: true },
    },
    title: { $: 'item.name' },
    subtitle: { $: 'item.brand' },
    badges: [{ tone: { $: 'item.status_tone' }, text: { $: 'item.status_label' } }],
  },
};

/** The session card the Photos grid lays out. */
const PHOTO_CARD = {
  type: 'Card',
  variant: 'media-top',
  props: {
    layout: 'media-top',
    aspect: '3/4',
    media: { src: { $: 'item.photo_url' }, ratio: '3/4', fit: 'cover' },
    badges: [{ tone: 'accent', text: { $: 'item.model_label' } }],
    meta: [{ value: { $: 'item.piece_count' } }],
  },
};

// A container sample's children arrive as an ARRAY, so each needs its own key.
const box = (label) => (
  <div key={label} style={{
    background: 'var(--surface-3)', borderRadius: 'var(--r-sm)', padding: '10px 12px',
    fontSize: 'var(--t-sm)', color: 'var(--text-2)',
  }}>{label}</div>
);

export const SAMPLES = {
  // ── L1 ────────────────────────────────────────────────────────────────
  Text: { variant: 'title', props: { value: 'Large title', tone: 'text' } },
  Button: { variant: 'primary', props: { label: 'Log a meal', icon: 'camera' } },
  Icon: { props: { name: 'camera', size: 26 } },
  Image: { variant: 'cover', props: { src: pieces[0]?.image_url, fit: 'cover', ratio: '3/4' }, height: 160 },
  Badge: { variant: 'pill', props: { tone: 'warn', text: 'Expiring soon' } },
  // The generic gauge, drawn PAST its threshold so the sample shows the one
  // rule the primitive seals: over-max is a tone change, never a clipped arc.
  // The RING variant, because the ring is the reading the merge would have
  // lost if the shape had stopped being a first-class option.
  Gauge: {
    variant: 'ring',
    props: { value: 7, max: 6, size: 96, stroke: 10, thresholds: [{ at: 6, tone: 'warn' }], label: '7 of 6' },
    children: <div style={{ fontSize: 'var(--t-lg)', fontWeight: 700 }}>7</div>,
    height: 116,
  },
  Overlay: {
    props: {},
    children: [
      box('base layer'),
      <span key="over" style={{
        background: 'var(--accent)', color: 'var(--accent-ink)', borderRadius: 'var(--r-pill)',
        padding: '2px 8px', fontSize: 'var(--t-xs)', fontWeight: 700,
      }}>over the base</span>,
    ],
    height: 92,
  },
  // Motion + the plate a row with no picture earns. The video sample points at
  // a source that will not load, which is the honest picture of the seal: the
  // POSTER stands in, exactly as it does under reduced motion.
  Video: {
    variant: 'inline',
    props: { src: 'https://cdn.example/clip.mp4', poster: PHOTO, ratio: '3/4', loop: true, muted: true },
    height: 200,
  },
  Monogram: { variant: 'square', props: { value: 'Fig Leaf Studio', ratio: '1/1', size: 72 }, height: 96 },
  // The layout spine: Vertical/Horizontal are the 1-D flex lines (Qt VBox/HBox);
  // Stack is the z-overlap strip only (plates SIT ON each other); Grid wraps.
  Vertical: { props: { gap: '2' }, children: [box('top'), box('middle'), box('bottom')] },
  Horizontal: { props: { gap: '2' }, children: [box('left'), box('center'), box('right')] },
  Stack: { props: { overlap: -8 }, children: [box('A'), box('B'), box('C')] },
  Grid: { props: { columns: 3, gap: '2' }, children: [box('1'), box('2'), box('3'), box('4'), box('5'), box('6')] },
  Surface: {
    variant: 'card', props: { padding: 'var(--sp-3)' },
    // 在原有卡片示例里展示无装饰底色与图片背景，内部留白复用真实 Padding。
    children: <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {box('Card surface')}
      <Surface variant="plain" props={{ backgroundColor: '#eaf4ed' }}>
        <Padding props={{ left: 32, top: 12, right: 12, bottom: 12 }}>Plain · background color</Padding>
      </Surface>
      <Surface variant="plain" props={{ backgroundImage: PHOTO, backgroundColor: '#eaf4ed', radius: 8 }}>
        <Padding props={{ left: 16, top: 24, right: 16, bottom: 24 }}>
          <span style={{ background: 'var(--surface)', color: 'var(--text)', padding: 4 }}>Plain · background image</span>
        </Padding>
      </Surface>
    </div>,
  },
  // 四边留白示例不绘制卡片背景，可直接观察子内容的位置。
  Padding: { props: { left: 48, right: 12, top: 16, bottom: 8 }, children: box('Inset content · left 48 / top 16') },

  // ── L2 ────────────────────────────────────────────────────────────────
  SearchField: { props: { placeholder: 'Search medicine' } },
  // The FILTER reading of the one picker: an 'All' head it opens on, and no
  // clear, because an always-on filter row must never land on nothing.
  OptionSelect: {
    variant: 'single',
    props: {
      mode: 'single', layout: 'chips', allOption: true, clearable: false,
      options: [{ key: 'all', label: 'All' }, { key: 'kids', label: 'Kids' }, { key: 'adult', label: 'Adults' }],
    },
  },
  // The LAUNCHER reading of the same family: tiles that FIRE (each spreading
  // its own capture recipe) instead of a chip that stays lit.
  ToolGrid: {
    variant: 'grid',
    props: {
      columns: 4,
      tiles: [
        { key: 'scan', label: 'Smart scan', icon: 'camera' },
        { key: 'excel', label: 'To Excel', icon: 'excel', badge: 'HOT' },
        { key: 'text', label: 'Extract text', icon: 'text' },
        { key: 'all', label: 'All tools', icon: 'grid' },
      ],
    },
  },
  StatTileRow: {
    variant: 'status',
    props: {
      tappable: true,
      tiles: [
        { key: 'expired', label: 'Expired', tone: 'bad' },
        { key: 'soon', label: 'Soon', tone: 'warn' },
        { key: 'nodate', label: 'No date', tone: 'muted' },
      ],
    },
    data: { expired: 1, soon: 2, nodate: 1 },
  },
  Sheet: {
    variant: 'form',
    props: { title: 'Daily calorie goal' },
    slots: { open: { payload: {}, seq: 1 } },
    children: box('Sheet content'),
    height: 220,
  },
  EmptyState: {
    variant: 'first-run',
    props: {
      kind: 'first-run', title: 'Your wardrobe is empty',
      body: 'Add your first piece — snap a garment or a full outfit photo.',
      cta: { label: 'Get Started', action: 'open_capture' },
    },
    ctx: { entries: [], pending: { adds: [] } },
  },
  // Three readings in one sample: not-yet-a-session, in flight, and TERMINAL —
  // where the shimmer stops and the cell stays tappable.
  PendingCard: {
    variant: 'row',
    props: { shape: 'row', hasImage: true },
    data: [{ client_ref: 'p1' }, { session_id: 's9' }, { session_id: 's8', status: 'finished' }],
    height: 220,
  },
  ErrorState: {
    variant: 'page',
    props: {
      title: 'We could not load this',
      body: 'The connection timed out. Your data is safe — try again.',
      retry: true, maxRetries: 3,
      support: { label: 'Contact support', action: 'contact_support' },
    },
    height: 260,
  },
  // A CARD reads the `item` scope, so its bag carries the row a grid would
  // have scoped — the same spec medicine-box and nutritionist declare.
  Card: {
    variant: 'media-left',
    props: {
      layout: 'media-left',
      aspect: '1/1',
      media: { src: { $: 'item.image_url' }, ratio: '1/1', fit: 'cover' },
      title: { $: 'item.name' },
      subtitle: { $: 'item.portion' },
      meta: [
        { value: { $: 'item.calorie_label' } },
        { value: { $: 'item.macro_label' } },
        { meter: { $: 'item.confidence_level' }, max: 3, label: 'confidence' },
      ],
      badges: [{ tone: { $: 'item.safety_tone' }, text: { $: 'item.safety_text' }, dot: true }],
    },
    ctx: withItem(mealRows.find((m) => m.safety_text) || mealRows[0]),
    height: 120,
  },
  // The advisory reading. The other one (`variant: 'legend'`) is the same bound
  // rows with a `count` — a tally beside a ring — which notes-taker draws.
  NoticeList: {
    props: {
      // the rows `safetyList` actually emits: the verdict word is the eyebrow,
      // the line underneath is WHAT was flagged and then why
      items: [
        { tone: 'bad', label: 'Avoid', text: 'Tonkotsu ramen · The broth is made with shellfish stock, which you have flagged as an allergy.' },
        { tone: 'warn', label: 'Caution', text: 'Chicken caesar salad · Caesar dressing is high in sodium — go easy if you are watching blood pressure.' },
      ],
    },
  },
  MediaPicker: { variant: 'grid', props: { max: 4, add: true }, data: [pieces[0]?.image_url, pieces[1]?.image_url] },
  // ONE page, which is the shape the edit surface opens on: the strip, the
  // rotate and the two verbs are all readable at a glance and the pager stays
  // out of it. The sample also PROVES the capability gate — the `filters` here
  // are the full v1 request, and what draws is the intersection with what this
  // build can apply.
  ScanEditor: {
    variant: 'page',
    props: {
      images: [pieces[0]?.image_url],
      filters: ['gray', 'bw', 'auto', 'contrast'],
      confirmLabel: 'File document',
    },
    height: 420,
  },
  BarChart: {
    variant: 'week',
    props: { target: 2200 },
    data: {
      week_start: '—',
      anchor: 'd4',
      bars: ['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((label, i) => ({
        date: `d${i}`, label, value: [1800, 2400, 1500, 2100, 1870, 0, 0][i], future: i > 4, selected: i === 4,
      })),
    },
  },

  // ── L2 blocks · inputs · detail media ─────────────────────────────────
  SectionHeader: {
    variant: 'section',
    props: { title: 'Key points', count: 5, action: { label: 'See all', action: 'open_all' } },
  },
  KeyValueList: {
    variant: 'rows',
    props: {
      items: [
        { label: 'Brand', value: medRows[0]?.brand || 'Acme' },
        { label: 'Category', value: medRows[0]?.category || 'General' },
        { label: 'Unknown', value: '' },
      ],
    },
  },
  TextBlock: {
    variant: 'prose',
    props: {
      variant: 'prose',
      value: [
        { block: 'heading', text: 'What was decided' },
        { block: 'paragraph', text: 'The team agreed to ship the rail first and keep the list mode behind the same switch.' },
        { block: 'bullets', items: ['Rail ships first', 'List stays reachable'] },
        { block: 'chips', items: [{ text: '→ action', tone: 'accent' }, { text: '? question', tone: 'warn' }] },
      ],
    },
    height: 220,
  },
  Disclosure: {
    props: { summary: '✦ Improve this draft', open: true },
    children: box('the folded content'),
    height: 140,
  },
  Timeline: {
    variant: 'row',
    props: {
      nodes: [
        { node: '1', primary: 'Mon', secondary: 'Draft', tone: 'good' },
        { node: '2', primary: 'Wed', secondary: 'Review', tone: 'accent' },
        { node: '3', primary: 'Fri', secondary: 'Ship', tone: 'warn' },
      ],
    },
    height: 110,
  },
  ModeSwitch: {
    variant: 'segmented',
    props: { variant: 'segmented', options: [{ key: 'calendar', label: 'Calendar' }, { key: 'list', label: 'List' }] },
    children: box('the active mode’s body'),
    height: 140,
  },
  TextComposer: {
    props: { placeholder: 'What should change?', submitLabel: 'Send to chat', rows: 2 },
    height: 170,
  },
  // The bounded number in its DEAD state, which is the rule it seals: the field
  // opens on the value it already holds, so there is nothing to save yet.
  NumberField: {
    props: {
      min: 800, max: 6000, step: 10, stepBy: 50, value: 2200,
      unit: 'per day', submitLabel: 'Set target', label: 'Daily target',
    },
    height: 230,
  },
  // The ROLE FORM is the WHOLE material-prep page, so its sample is that page:
  // the counted headline, the intro, the section rule and two numbered role
  // cards carrying the shipped copy shape (eyebrow · title · description).
  //
  // MULTI-ROLE, UNTOUCHED — the state the page actually opens in, both wells
  // inviting and the submit reading `Add photos above`.
  //
  // It used to seed one answered well with a `slots: {setValue}` bag, and that
  // is exactly what the form must NOT honour: the slot store outlives the page
  // (`ViewIRPage` is never remounted across a navigation and nothing dispatches
  // the reducer's `reset`), so a delivery present at MOUNT is the previous
  // visit's answer, and replaying it refills a well with the last session's
  // photo. Both kits mark a pre-mount delivery seen — see `useDelivery` in
  // `RoleMediaForm.jsx` — so the preview cannot fake the answered lane, and
  // should not: the filled/half/complete readings are asserted in
  // `roleMediaForm.test.jsx`, where a delivery can arrive AFTER mount the way a
  // host's really does.
  RoleMediaForm: {
    props: {
      roles: [
        {
          id: 'person_a',
          label: 'Person A',
          title: 'Late loved one',
          description: 'The person you want to remember. They appear on the left with the wings.',
        },
        {
          id: 'person_b',
          label: 'Person B',
          title: 'Living loved one',
          description: 'The person sharing the reunion. They appear on the right.',
        },
      ],
      compositionReference: pieces[3]?.photo_url || PHOTO,
      compositionPosition: 'before',
      compositionRole: 'composition_reference',
      tip: 'Clear, front-facing photos provide the best identity match.',
    },
    height: 620,
  },
  // The PLACED plate, in its tappable reading: four boxes at authored `%`
  // coordinates, two of them filled from the bound rows and two still inviting.
  SlotBoard: {
    variant: 'board',
    props: {
      variant: 'board', aspect: '1 / 1.2', texture: 'dots',
      slots: [
        { cat: 'tops', x: 8, y: 5, w: 40, h: 29, label: 'Tops' },
        { cat: 'jacket', x: 52, y: 4, w: 40, h: 32, label: 'Layer' },
        { cat: 'jeans', x: 8, y: 40, w: 40, h: 36, label: 'Bottoms' },
        { cat: 'shoes', x: 50, y: 72, w: 38, h: 24, label: 'Shoes' },
      ],
    },
    data: pieces,
    ctx: baseCtx,
    height: 320,
  },
  // The MEASURED fan: one root box, three bound rows, one edge each.
  ConnectorLayer: {
    props: {
      from: { type: 'Badge', props: { tone: 'accent', text: '3 topics' } },
      to: { type: 'TextBlock', props: { value: { $: 'item.title' } } },
      tones: ['accent', 'good', 'warn'],
      curve: 'bezier',
      renderTemplate,
    },
    data: [
      { title: 'Scope for the first release' },
      { title: 'Who owns the migration' },
      { title: 'What we do about the backlog' },
    ],
    ctx: baseCtx,
    height: 200,
  },
  Carousel: {
    props: { ratio: '3/4', fit: 'contain', backdrop: true, dots: true, thumbs: true },
    data: [PHOTO, pieces[0]?.image_url, pieces[1]?.image_url],
    height: 300,
  },
  BeforeAfterCompare: {
    props: { before: pieces[0]?.image_url, after: pieces[1]?.image_url, reveal: 45, ratio: '1/1' },
    height: 240,
  },

  // ── L2 capability (generic too — they own the lifecycle, not the pixels) ─
  PageShell: {
    variant: 'dashboard',
    props: { title: 'Medicine Box', eyebrow: 'AI grouped · 6 items', layout: 'dashboard' },
    children: box('page body'),
    height: 220,
  },
  // The DEFAULT shape a collection IS: one grid laying out `Card`s,
  // so the bootstrap card reads "grid composes cards" — the hierarchy it exists
  // to show. Group mode (a section heading per bucket) is a variant a blueprint
  // opts into, exercised by medicine-box, not what the component is.
  ItemCollection: {
    props: {
      columns: 3, gap: '3', aspect: '1/1',
      card: MED_CARD,
      renderTemplate,
    },
    data: medRows.slice(0, 6),
    ctx: baseCtx,
    height: 300,
  },
  SessionCollection: {
    variant: 'grid',
    props: { columns: 2, gap: '3', aspect: '3/4', card: PHOTO_CARD, renderTemplate },
    data: photos,
    ctx: baseCtx,
    height: 260,
  },
  ItemCalendar: {
    props: { dateField: 'date', selected: DATED_ROWS[0].date, renderTemplate },
    data: DATED_ROWS,
    ctx: baseCtx,
    height: 320,
  },
  ItemPage: {
    props: {
      sections: [
        { type: 'hero', ratio: '3/4' },
        { type: 'kv', fields: ['name', 'brand', 'category', 'expiry', 'dosage'] },
      ],
    },
    data: medRows[0],
    ctx: baseCtx,
    height: 340,
  },
  SessionPage: {
    props: { sections: [{ type: 'carousel' }, { type: 'kv', fields: ['session_id', 'piece_count'] }] },
    data: { ...photos[0], images: [PHOTO, pieces[0]?.image_url, pieces[1]?.image_url] },
    ctx: baseCtx,
    height: 340,
  },
  host: { props: { visible: true } },

  // ── THE SESSION BLOCKS (v0.9 `native-ir`) ─────────────────────────────
  //
  // A session block's bag is the SIMPLEST one on this page, and that is the
  // fact worth seeing: its `props` ARE the model's authored `data`, verbatim.
  // There is no `ctx` and no `data` here because a block has neither — it is
  // an immutable snapshot of one answer, so nothing it draws was ever read
  // from a feed. Each sample is the PRD's own 样张 for that block (§4A), so
  // the bootstrap card is a picture of the contract rather than of a mock.
  hero_media: {
    props: {
      imageUrl: PHOTO,
      title: 'Your living room, restyled',
      subtitle: 'Japandi · warm oak, linen, low profile',
    },
    height: 400,
  },
  // The comparison is its OWN block; `hero_media` above is one picture.
  before_after: {
    props: {
      beforeImageUrl: pieces[0]?.image_url,
      afterImageUrl: pieces[1]?.image_url,
      title: 'Your living room, restyled',
      subtitle: 'Drag the divider to compare',
    },
    height: 400,
  },
  text: {
    props: {
      title: 'What you’re looking at',
      content: 'A classic avocado toast with poached eggs — roughly 470 kcal for the plate as photographed.',
    },
    height: 140,
  },
  markdown: {
    props: {
      title: 'Care instructions',
      paragraphs: [
        'Bright, **indirect** light. Water when the top 2 cm are dry.',
        'Repot every two years, into a pot one size up with free-draining soil.',
      ],
      footer: 'Based on Crassula ovata guidelines',
    },
    height: 190,
  },
  agent_speech: {
    props: { text: 'Nice shot — that’s a jade plant in a ceramic pot. Want a watering schedule for it?' },
    height: 80,
  },
  stat_grid: {
    props: {
      items: [
        { label: 'Calories', value: '470', unit: 'kcal' },
        { label: 'Carbs', value: '58', unit: 'g' },
        { label: 'Protein', value: '24', unit: 'g' },
        { label: 'Fat', value: '12', unit: 'g' },
      ],
    },
    height: 190,
  },
  // A THREE-column table: the middle column is prose that wraps, the last one is a
  // short number. One sample per component id, so this is the wide read — the one
  // that used to crowd its right-hand column — rather than the 2-col `.kv` read.
  table: {
    props: {
      title: 'Menu sections',
      headers: ['Section', 'Signature dish', 'Price'],
      rows: [
        ['Small plates', 'Charred padrón peppers with lemon salt', '€6–9'],
        ['From the grill', 'Galician blonde ribeye, 45-day aged, served for two', '€48'],
        ['Sweet', 'Basque burnt cheesecake with sherry cream', '€7'],
      ],
      footer: 'Read off the menu photographed on 12 May.',
      // the OPTIONAL leading picture column (B18 P-A) — one entry per row, and
      // the middle one is deliberately absent: a row with no picture keeps its
      // place in the column instead of shifting the names
      thumbnails: [PHOTO, '', pieces[1]?.image_url],
    },
    height: 260,
  },
  chip_list: {
    props: {
      title: 'Detected items',
      chips: ['Poached eggs', 'Sourdough toast', 'Olive oil', 'Roasted tomatoes', 'Avocado mash'],
      selected: [0, 1],
    },
    height: 170,
  },
  icon_list: {
    props: {
      title: 'Ingredients',
      items: [
        { image: pieces[0]?.image_url, name: 'Free-range eggs', detail: '2 large' },
        { image: pieces[1]?.image_url, name: 'Avocado', detail: '1 ripe' },
        // …and the row with no picture, which still draws: the name is the
        // content and the thumb is decoration.
        { name: 'Sourdough slice', detail: '×2' },
      ],
    },
    height: 210,
  },
  numbered_steps: {
    props: {
      title: 'How to make it',
      steps: [
        'Toast the sourdough until golden.',
        'Poach the eggs three minutes at a gentle simmer.',
        'Mash the avocado, season, assemble.',
      ],
    },
    height: 220,
  },
  image_gallery: {
    props: {
      title: 'Generated views',
      images: [
        { url: PHOTO, caption: 'Warm oak' },
        { url: pieces[0]?.image_url, caption: 'Linen' },
        { url: pieces[1]?.image_url, caption: 'Low profile' },
      ],
      footer: '3 renders · tap to open',
    },
    height: 330,
  },
  link_preview: {
    props: {
      links: [
        {
          url: 'https://www.thespruce.com/jade-plant-care',
          title: 'Jade plant care — complete guide',
          description: 'Light, water, soil and repotting for Crassula ovata, with a seasonal schedule.',
          domain: 'thespruce.com',
        },
        // the one that failed to enrich: no title, no description, no picture
        // — and still a working link, drawn from its own host.
        { url: 'https://www.rhs.org.uk/plants/crassula' },
      ],
    },
    height: 240,
  },
  location: {
    props: {
      lat: 22.2823,
      lng: 114.1285,
      label: 'Kennedy Town, Hong Kong',
      address: 'Belcher’s Street, Western District, Hong Kong Island',
    },
    height: 260,
  },
  shopping_list: {
    props: {
      title: 'Where to shop',
      items: [
        // the per-item product photo a shopping search returns (B18 P-A). The
        // second row has none — the column is still drawn, so both names line up
        { name: 'Dark red metal table lamp', brand: 'H&M Home', price: '$60.00', image: pieces[2]?.image_url, buyUrl: 'https://example.com/lamp' },
        { name: 'Succulent soil mix', brand: 'Miracle-Gro', quantity: '2 bags', price: '$8.00' },
      ],
      totalEstimate: '$68.00',
      storeSuggestion: 'Both are stocked at your nearest branch.',
    },
    height: 260,
  },
  video_player: {
    props: {
      title: 'Room makeover — generated',
      url: 'https://cdn.example/room.mp4',
      poster: PHOTO,
      description: 'A slow pan across the restyled room.',
      duration: '00:08',
    },
    height: 320,
  },
  file_card: {
    props: {
      // minted AT DELIVERY, so the sample is the state it actually ships in:
      // `url` already resolved, never a spinner
      title: 'Lease agreement — extracted.xlsx',
      url: 'https://cdn.example/lease-agreement.xlsx',
      fileType: 'xlsx',
      fileName: 'lease-agreement.xlsx',
      size: '48 KB',
      pageCount: 12,
      description: 'Every clause table on one sheet, one row per clause.',
    },
    height: 190,
  },
  worker_delegate: {
    // the state a delegate spends its life in: still working, with the first
    // deliverable already landed under it
    props: {
      title: 'Preparing your report',
      task: 'worker-standard',
      status: 'delivering',
      slot: 'wd-9b77b21a',
      progressStage: 'uploading',
      deliverables: [
        {
          slot: 'wd-9b77b21a-1', kind: 'file',
          url: 'https://cdn.example/price-report.pdf',
          fileName: 'price-report.pdf', fileType: 'pdf',
        },
      ],
    },
    height: 280,
  },
  action_bar: {
    props: {
      actions: [
        { label: 'Save', action: 'macroShare', icon: 'save' },
        { label: 'Ask again', action: 'openSession', icon: 'chevron' },
        { label: 'Re-run', action: 'reloadData', icon: 'redo' },
        // …and one the kit does not know: DROPPED, never drawn dead.
        { label: 'Teleport', action: 'notAThing', icon: 'star' },
      ],
    },
    height: 92,
  },

  // ── L3 Domain (a COMPOSITION, not code) ───────────────────────────────
  // Its bag is the one the interpreter would build for the blueprint's `hero`
  // node: resolved `props` (the parameter interface) + the `todayTotals` row
  // its `bind` resolves to, preformatted labels and all — the composition
  // reads `props.*` and `data.*`, so this is the real thing, not a mock.
  CaloryRing: {
    variant: 'ring',
    props: { value: 1870, target: 2200, unit: 'cal' },
    data: {
      calories: 1870, calories_low: 1610, calories_high: 2180, remaining: 330,
      calories_label: '1,870', target_label: '2,200',
      range_label: '1,610–2,180', remaining_label: '330 left',
    },
    height: 260,
  },
};

/**
 * The registry components THIS one lays out, derived from its own sample —
 * and, for an L3 Domain component, from its `composition` body.
 *
 * A grid is a grid OF something: its sample carries the real `card` template it
 * renders per row, so the answer is already in the bag — walk the sample's
 * props for any nested node whose `type` the kit knows. A COMPOSED component is
 * literally nothing but the types it lays out, so its body is walked the same
 * way. Derived rather than listed per component, so a sample that starts
 * nesting a node says so without anyone remembering to update a table. A leaf
 * nests nothing and returns [], which the card reads as "say nothing" rather
 * than "draw an empty heading".
 *
 * `$$typeof` skips React elements: a container sample's `children` are rendered
 * boxes, not blueprint nodes, and their `.type` is a DOM tag.
 */
export function composedTypes(id) {
  const out = [];
  const walk = (v) => {
    if (Array.isArray(v)) { v.forEach(walk); return; }
    if (!v || typeof v !== 'object' || v.$$typeof) return;
    if (typeof v.type === 'string' && lookupKit(v.type)) out.push(v.type);
    Object.values(v).forEach(walk);
  };
  walk(SAMPLES[id]?.props);
  walk(lookupKit(id)?.composition);
  return [...new Set(out)];
}
